import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  User,
  UserPlus,
  Mic,
  FileText,
  Gem,
  BookOpen,
  Bell,
  Download,
  Flame,
  ChevronDown,
  Check,
  X,
} from 'lucide-react';
import { Button } from '../ui/button';
import { useOnboarding } from '../onboarding/OnboardingContext';
import { useBuildArchiveProgress } from '../../utils/buildArchiveProgress';
import { buildTreePeopleAddPersonUrl } from '../../utils/archiveNavigation';
import { treeHref, personHref } from '../../utils/prettyArchiveLinks';
import { getMyArchivePersonId } from '../../utils/myArchivePerson';
import { markGuideMemorialExplored } from '../../utils/guideProgress';
import { cn } from '@/lib/utils';
import { CORE_JOURNEY_UI } from '../../config/testerRelease';

function stepStatus(complete, started) {
  if (complete) return 'complete';
  if (started) return 'started';
  return 'not_started';
}

const STATUS_COPY = {
  not_started: 'Not started',
  started: 'In progress',
  complete: 'Complete',
};

/**
 * Dashboard “Build your archive” — suggested next steps, not gamification.
 */
export function BuildArchiveChecklist({
  user,
  trees = [],
  onOpenCreateTree,
  className = '',
  onOpenExport,
}) {
  const { progress, loading } = useBuildArchiveProgress(trees, user);
  const {
    setDashboardChecklistMinimized,
    setDashboardChecklistDismissed,
  } = useOnboarding();

  const firstTree = trees[0];
  const selfId = user?.self_person_id || getMyArchivePersonId();

  const items = useMemo(() => {
    const p = progress || {};
    const peopleBeyondSelf = Math.max(0, (p.peopleCount || 0) - (p.hasSelf ? 1 : 0));

    return [
      {
        id: 'self',
        icon: User,
        title: 'Add yourself as a person',
        hint: 'Link your profile in the tree.',
        status: stepStatus(p.hasSelf, p.peopleCount > 0),
        action: selfId ? (
          <Button size="sm" className="rounded-full" asChild>
            <Link to={personHref({ id: selfId })}>Open my profile</Link>
          </Button>
        ) : firstTree ? (
          <Button size="sm" className="rounded-full" asChild>
            <Link to={`${treeHref(firstTree)}?tab=grid`}>Find me in People</Link>
          </Button>
        ) : (
          <Button size="sm" className="rounded-full" onClick={onOpenCreateTree}>
            Create an archive first
          </Button>
        ),
      },
      {
        id: 'member',
        icon: UserPlus,
        title: 'Add your first family member',
        hint: 'Start with one person who matters.',
        status: stepStatus(peopleBeyondSelf >= 1 || (p.peopleCount >= 2), p.peopleCount >= 1),
        action: firstTree ? (
          <Button size="sm" variant="outline" className="rounded-full" asChild>
            <Link to={buildTreePeopleAddPersonUrl(firstTree.id)}>Add a person</Link>
          </Button>
        ) : (
          <Button size="sm" variant="outline" className="rounded-full" onClick={onOpenCreateTree}>
            Create an archive
          </Button>
        ),
      },
      {
        id: 'memory',
        icon: Mic,
        title: 'Write or record your first memory',
        hint: 'A short story or voice note is enough.',
        status: stepStatus(p.storyCount > 0, false),
        action: selfId ? (
          <Button size="sm" variant="outline" className="rounded-full" asChild>
            <Link to={`${personHref({ id: selfId })}?view=stories`}>Record voice memory</Link>
          </Button>
        ) : firstTree ? (
          <Button size="sm" variant="outline" className="rounded-full" asChild>
            <Link to={buildTreePeopleAddPersonUrl(firstTree.id)}>Add someone first</Link>
          </Button>
        ) : null,
      },
      {
        id: 'document',
        icon: FileText,
        title: 'Upload a document or photo',
        hint: 'Photos, letters and certificates linked to a person.',
        status: stepStatus(p.documentCount > 0, false),
        action: selfId ? (
          <Button size="sm" variant="outline" className="rounded-full" asChild>
            <Link to={`${personHref({ id: selfId })}?view=documents`}>Preserve a document</Link>
          </Button>
        ) : null,
      },
      {
        id: 'possession',
        icon: Gem,
        title: 'Add a priceless possession',
        hint: 'Objects that carry meaning.',
        status: stepStatus(p.possessionCount > 0, false),
        action: firstTree ? (
          <Button size="sm" variant="outline" className="rounded-full" asChild>
            <Link to={`${treeHref(firstTree)}?tab=memories`}>Add a possession</Link>
          </Button>
        ) : null,
      },
      {
        id: 'storybook',
        icon: BookOpen,
        title: 'Start Storybook Mode',
        hint: 'Guided chapters in AI Story Studio — one prompt at a time.',
        status: stepStatus(p.hasStorybook, false),
        action: (
          <Button size="sm" variant="outline" className="rounded-full" asChild>
            <Link to="/story-studio">Open Story Studio</Link>
          </Button>
        ),
      },
      {
        id: 'reminder',
        icon: Bell,
        title: 'Create a reminder',
        hint: 'Birthdays, remembrance days, anniversaries.',
        status: stepStatus(p.reminderCount > 0, false),
        action: (
          <Button size="sm" variant="outline" className="rounded-full" asChild>
            <Link to="/reminders">Add reminder</Link>
          </Button>
        ),
      },
      {
        id: 'export',
        icon: Download,
        title: 'Preview your Legacy Book',
        hint: 'Preview how your archive gathers into a keepsake PDF.',
        status: stepStatus(p.exportPreviewed, false),
        action: firstTree ? (
          <Button
            size="sm"
            variant="outline"
            className="rounded-full"
            onClick={() => onOpenExport?.(firstTree)}
          >
            Preview Legacy Book
          </Button>
        ) : null,
      },
      {
        id: 'memorial',
        icon: Flame,
        title: 'Explore Memorial Mode',
        hint: 'Optional tribute pages — private until you publish.',
        status: stepStatus(p.memorialExplored || p.hasMemorialWorkspace, p.hasMemorialWorkspace),
        action: (
          <Button
            size="sm"
            variant="outline"
            className="rounded-full"
            asChild
            onClick={() => markGuideMemorialExplored()}
          >
            <Link to="/memorials">Open memorials</Link>
          </Button>
        ),
      },
    ];
  }, [progress, firstTree, selfId, onOpenCreateTree, onOpenExport]);

  const CORE_CHECKLIST_IDS = new Set(['self', 'member', 'memory', 'document', 'export']);
  const visibleItems = CORE_JOURNEY_UI ? items.filter((i) => CORE_CHECKLIST_IDS.has(i.id)) : items;

  const completedCount = visibleItems.filter((i) => i.status === 'complete').length;
  const nextItem = visibleItems.find((i) => i.status !== 'complete');

  return (
    <aside
      className={cn(
        'rounded-2xl border border-[hsl(38_16%_86%)] bg-[hsl(42_26%_99%)] shadow-[0_8px_32px_-20px_hsl(28_14%_18%_/_0.1)]',
        className
      )}
      data-testid="build-archive-checklist"
    >
      <div className="flex items-start justify-between gap-3 border-b border-[hsl(38_14%_88%)]/80 px-5 py-4">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/70">
            Suggested next steps
          </p>
          <h3 className="mt-1 font-serif text-lg font-light text-[hsl(24_12%_18%)]">
            {CORE_JOURNEY_UI ? 'Your first steps' : 'Build your archive'}
          </h3>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
            {loading ? 'Checking progress…' : `${completedCount} of ${visibleItems.length} done`}
          </p>
        </div>
        <div className="flex shrink-0 gap-1">
          <button
            type="button"
            className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted/40"
            onClick={() => setDashboardChecklistMinimized(true)}
            aria-label="Minimize checklist"
          >
            <ChevronDown className="h-4 w-4" />
          </button>
          <button
            type="button"
            className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted/40"
            onClick={() => setDashboardChecklistDismissed(true)}
            aria-label="Dismiss checklist"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {nextItem ? (
        <div className="border-b border-[hsl(38_14%_88%)]/60 bg-[#c9a961]/5 px-5 py-3">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-[#8b7355]">Start here</p>
          <p className="mt-0.5 text-sm font-medium text-foreground/90">{nextItem.title}</p>
        </div>
      ) : null}

      <ul className="max-h-[min(28rem,60vh)] space-y-0 overflow-y-auto px-3 py-3">
        {visibleItems.map((item) => {
          const Icon = item.icon;
          const done = item.status === 'complete';
          return (
            <li
              key={item.id}
              className={cn(
                'rounded-xl px-3 py-3',
                item.id === nextItem?.id && !done && 'bg-white/80 ring-1 ring-[#c9a961]/20'
              )}
            >
              <div className="flex gap-3">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-[hsl(38_14%_86%)] bg-[hsl(42_28%_99%)] text-[#8b7355]">
                  {done ? <Check className="h-4 w-4 text-[#2C4A3B]" /> : <Icon className="h-4 w-4" strokeWidth={1.35} />}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-medium text-foreground/90">{item.title}</p>
                    <span className="text-[10px] text-muted-foreground">{STATUS_COPY[item.status]}</span>
                  </div>
                  <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{item.hint}</p>
                  {item.action ? <div className="mt-2 flex flex-wrap gap-2">{item.action}</div> : null}
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      <div className="border-t border-[hsl(38_14%_88%)]/80 px-5 py-3 text-center">
        <Link to="/guide" className="text-xs font-medium text-[#8b7355] hover:text-[#5c4a32]">
          Full feature guide →
        </Link>
      </div>
    </aside>
  );
}

export default BuildArchiveChecklist;
